import type { User } from '../state/zustand';
import { isDefined } from '@seaber/common/lib/generics';
import {
  isValidRate,
  type RateInfo,
} from '../components/core/Core.loading-discharging';
import type { CalculationConfig } from '../components/core/ScheduleDataContext';
import type { LocationId } from '@seaber/common/graphql/types';
import { nonWeakMemoize6 } from '@seaber/common/lib/memoize';

export type PortCommodityLdRateSource = 'port' | 'zone';

export type LdRates = {
  loading: RateInfo;
  discharging: RateInfo;
};

export type LdRateKey =
  `${PortCommodityLdRateSource}-${number}-${`assortment-${number}` | `assortmentGroup-${number}`}`;

export const transformPortCommodityLdRates = (
  portCommodityLdRates?: NonNullable<
    User['organization']
  >['portCommodityLdRatesByOrganizationId']['nodes'],
): Map<LdRateKey, LdRates> => {
  const result = new Map<LdRateKey, LdRates>();
  portCommodityLdRates?.filter(isDefined).forEach((rate) => {
    const {
      portId,
      zoneId,
      loadingRate,
      dischargeRate,
      dischargeUnit,
      assortmentId,
      assortmentGroupId,
    } = rate;

    const rateData = {
      loading: {
        rate: Number(loadingRate),
        unit: rate.loadingUnit,
      },
      discharging: {
        rate: Number(dischargeRate),
        unit: dischargeUnit,
      },
    };

    if (isDefined(assortmentId)) {
      if (portId) {
        result.set(`port-${portId}-assortment-${assortmentId}`, rateData);
      } else if (zoneId) {
        result.set(`zone-${zoneId}-assortment-${assortmentId}`, rateData);
      }
    }

    if (isDefined(assortmentGroupId)) {
      if (portId) {
        result.set(
          `port-${portId}-assortmentGroup-${assortmentGroupId}`,
          rateData,
        );
      } else if (zoneId) {
        result.set(
          `zone-${zoneId}-assortmentGroup-${assortmentGroupId}`,
          rateData,
        );
      }
    }
  });

  return result;
};

const isLoadEventType = (eventType: 'load' | 'discharge'): boolean =>
  eventType === 'load';
const isDischargeEventType = (eventType: 'load' | 'discharge'): boolean =>
  eventType === 'discharge';

/**
 * Helper to get the loading and discharging rates for a given order's loading and discharge ports' zones, if available
 * Zones are fetched to the config under `locationsById`. A port can technically have multiple zones where it's assigned to,
 * but we'll just return the first match that has rates.
 */
export const getZoneLdRates = ({
  loadPortId,
  dischargePortId,
  config,
  eventType,
  assortmentId,
  assortmentGroupId,
}: {
  loadPortId: LocationId | null | undefined;
  dischargePortId: LocationId | null | undefined;
  config: CalculationConfig;
  eventType: 'load' | 'discharge';
  assortmentId?: number | null;
  assortmentGroupId?: number | null;
}): LdRates | null => {
  const findFirstLdRatesForZones = (
    zoneLocations: ({ zoneId: number } | null)[],
  ): LdRates | null => {
    if (!zoneLocations) return null;

    if (isDefined(assortmentId)) {
      for (const zoneLocation of zoneLocations) {
        if (isDefined(zoneLocation?.zoneId)) {
          const zoneLdRates = config.portCommodityLdRates?.get(
            `zone-${zoneLocation.zoneId}-assortment-${assortmentId}`,
          );
          if (zoneLdRates) return zoneLdRates;
        }
      }
    }

    if (isDefined(assortmentGroupId)) {
      for (const zoneLocation of zoneLocations) {
        if (isDefined(zoneLocation?.zoneId)) {
          const zoneLdRates = config.portCommodityLdRates?.get(
            `zone-${zoneLocation.zoneId}-assortmentGroup-${assortmentGroupId}`,
          );
          if (zoneLdRates) return zoneLdRates;
        }
      }
    }

    return null;
  };

  // For load events, check the load port
  if (isLoadEventType(eventType) && isDefined(loadPortId)) {
    const loadPort = config.locationsById.get(loadPortId);
    if (!loadPort?.zoneLocationsByLocationId?.nodes) return null;

    return findFirstLdRatesForZones(loadPort.zoneLocationsByLocationId.nodes);
  }

  // For discharge events, check the discharge port
  if (isDischargeEventType(eventType) && isDefined(dischargePortId)) {
    const dischargePort = config.locationsById.get(dischargePortId);
    if (!dischargePort?.zoneLocationsByLocationId?.nodes) return null;

    return findFirstLdRatesForZones(
      dischargePort.zoneLocationsByLocationId.nodes,
    );
  }

  return null;
};

export const getPortCommodityLdRates = nonWeakMemoize6(
  (
    config: CalculationConfig,
    eventType: 'load' | 'discharge',
    loadPortId: LocationId | null | undefined,
    dischargePortId: LocationId | null | undefined,
    assortmentId: number | null | undefined,
    assortmentGroupId: number | null | undefined,
  ): RateInfo => {
    if (!isDefined(assortmentId) && !isDefined(assortmentGroupId)) {
      return {
        rate: null,
        unit: null,
      };
    }

    const zoneLdRateConfig = {
      loadPortId,
      dischargePortId,
      config,
      eventType,
      assortmentId,
      assortmentGroupId,
    };

    if (isLoadEventType(eventType) && loadPortId) {
      if (isDefined(assortmentId)) {
        const portRates = config.portCommodityLdRates?.get(
          `port-${loadPortId}-assortment-${assortmentId}`,
        );
        if (isValidRate(portRates?.loading)) return portRates.loading;
      }

      if (isDefined(assortmentGroupId)) {
        const portGroupRates = config.portCommodityLdRates?.get(
          `port-${loadPortId}-assortmentGroup-${assortmentGroupId}`,
        );
        if (isValidRate(portGroupRates?.loading)) return portGroupRates.loading;
      }

      const zoneRates = getZoneLdRates(zoneLdRateConfig);
      if (isValidRate(zoneRates?.loading)) {
        return zoneRates.loading;
      }
    }

    if (isDischargeEventType(eventType) && dischargePortId) {
      if (isDefined(assortmentId)) {
        const portRates = config.portCommodityLdRates?.get(
          `port-${dischargePortId}-assortment-${assortmentId}`,
        );
        if (isValidRate(portRates?.discharging)) {
          return portRates.discharging;
        }
      }

      if (isDefined(assortmentGroupId)) {
        const portGroupRates = config.portCommodityLdRates?.get(
          `port-${dischargePortId}-assortmentGroup-${assortmentGroupId}`,
        );
        if (isValidRate(portGroupRates?.discharging)) {
          return portGroupRates.discharging;
        }
      }

      const zoneRates = getZoneLdRates(zoneLdRateConfig);

      if (isValidRate(zoneRates?.discharging)) {
        return zoneRates.discharging;
      }
    }

    return null;
  },
);
